import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usersAPI, driversAPI } from '@/lib/api';
import { formatDate, getTodayString } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  Calendar, 
  Users, 
  Truck, 
  MapPin,
  Plus,
  X,
  CalendarOff,
  CalendarCheck,
  User
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export default function AdminDrivers() {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [newUnavailableDate, setNewUnavailableDate] = useState(getTodayString());
  const [saving, setSaving] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    phone: '',
    territory: '',
  });

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll('driver');
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAvailabilityDialog = (driver) => {
    setSelectedDriver(driver);
    setNewUnavailableDate(getTodayString());
    setShowAvailabilityDialog(true);
  };

  const handleAddUnavailableDate = async () => {
    if (!newUnavailableDate || !selectedDriver) return;
    
    setSaving(true);
    try {
      await driversAPI.addUnavailableDate(selectedDriver.id, newUnavailableDate);
      toast.success(`${formatDate(newUnavailableDate)} marked as day off`);
      
      // Update local state
      setDrivers(prev => prev.map(d => 
        d.id === selectedDriver.id 
          ? { ...d, unavailable_dates: [...(d.unavailable_dates || []), newUnavailableDate].sort() }
          : d
      ));
      setSelectedDriver(prev => ({
        ...prev,
        unavailable_dates: [...(prev.unavailable_dates || []), newUnavailableDate].sort()
      }));
    } catch (error) {
      console.error('Error adding date:', error);
      toast.error('Failed to add date');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUnavailableDate = async (date) => {
    if (!selectedDriver) return;
    
    try {
      await driversAPI.removeUnavailableDate(selectedDriver.id, date);
      toast.success(`${formatDate(date)} removed`);
      
      // Update local state
      setDrivers(prev => prev.map(d => 
        d.id === selectedDriver.id 
          ? { ...d, unavailable_dates: (d.unavailable_dates || []).filter(dd => dd !== date) }
          : d
      ));
      setSelectedDriver(prev => ({
        ...prev,
        unavailable_dates: (prev.unavailable_dates || []).filter(dd => dd !== date)
      }));
    } catch (error) {
      console.error('Error removing date:', error);
      toast.error('Failed to remove date');
    }
  };

  const openEditDialog = (driver) => {
    setSelectedDriver(driver);
    setEditForm({
      full_name: driver.full_name,
      phone: driver.phone || '',
      territory: driver.territory || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateDriver = async () => {
    if (!selectedDriver) return;
    
    setSaving(true);
    try {
      await usersAPI.update(selectedDriver.id, editForm);
      toast.success('Driver updated');
      setShowEditDialog(false);
      fetchDrivers();
    } catch (error) {
      console.error('Error updating driver:', error);
      toast.error('Failed to update driver');
    } finally {
      setSaving(false);
    }
  };

  // Get upcoming unavailable dates for display
  const getUpcomingDaysOff = (driver) => {
    const today = getTodayString();
    const dates = (driver.unavailable_dates || [])
      .filter(d => d >= today)
      .sort()
      .slice(0, 3);
    return dates;
  };

  const territories = ['North', 'South', 'Downtown', 'Kingsport', 'General'];

  return (
    <AdminLayout title="Drivers">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#52B8E1]/20 rounded-lg">
                <Users className="text-[#194461]" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{drivers.length}</p>
                <p className="text-sm text-slate-600">Total Drivers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CalendarCheck className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {drivers.filter(d => !(d.unavailable_dates || []).includes(getTodayString())).length}
                </p>
                <p className="text-sm text-slate-600">Available Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <CalendarOff className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {drivers.filter(d => (d.unavailable_dates || []).includes(getTodayString())).length}
                </p>
                <p className="text-sm text-slate-600">Off Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Drivers List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      ) : drivers.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="text-slate-400" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Drivers Found</h3>
            <p className="text-slate-600">No drivers have been set up yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drivers.map((driver) => {
            const isOffToday = (driver.unavailable_dates || []).includes(getTodayString());
            const upcomingDaysOff = getUpcomingDaysOff(driver);
            
            return (
              <Card 
                key={driver.id} 
                className={`border-slate-200 ${isOffToday ? 'opacity-60' : ''}`}
                data-testid={`driver-card-${driver.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isOffToday ? 'bg-amber-100' : 'bg-[#52B8E1]/20'
                      }`}>
                        <User className={isOffToday ? 'text-amber-600' : 'text-[#194461]'} size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{driver.full_name}</h3>
                        <p className="text-sm text-slate-500">{driver.email}</p>
                      </div>
                    </div>
                    {isOffToday && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300" variant="outline">
                        Off Today
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    {driver.phone && (
                      <p className="text-slate-600">{driver.phone}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-slate-400" />
                      <span className="text-slate-600">
                        Territory: <span className="font-medium">{driver.territory || 'Not assigned'}</span>
                      </span>
                    </div>
                  </div>

                  {/* Upcoming Days Off */}
                  {upcomingDaysOff.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-3 mb-4">
                      <p className="text-xs font-medium text-slate-500 mb-2">Upcoming days off:</p>
                      <div className="flex flex-wrap gap-1">
                        {upcomingDaysOff.map(date => (
                          <Badge key={date} variant="outline" className="text-xs bg-white">
                            {formatDate(date)}
                          </Badge>
                        ))}
                        {(driver.unavailable_dates || []).filter(d => d >= getTodayString()).length > 3 && (
                          <span className="text-xs text-slate-400">
                            +{(driver.unavailable_dates || []).filter(d => d >= getTodayString()).length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => openEditDialog(driver)}
                      data-testid={`edit-driver-${driver.id}`}
                    >
                      Edit Info
                    </Button>
                    <Button 
                      className="flex-1 bg-[#194461] hover:bg-[#091D2E]"
                      onClick={() => openAvailabilityDialog(driver)}
                      data-testid={`manage-availability-${driver.id}`}
                    >
                      <Calendar size={14} className="mr-1" />
                      Availability
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Availability Dialog */}
      <Dialog open={showAvailabilityDialog} onOpenChange={setShowAvailabilityDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Availability</DialogTitle>
            <DialogDescription>
              {selectedDriver?.full_name} - Mark days off
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Add new date */}
            <div className="flex gap-2">
              <Input
                type="date"
                value={newUnavailableDate}
                onChange={(e) => setNewUnavailableDate(e.target.value)}
                min={getTodayString()}
                className="flex-1"
                data-testid="new-date-input"
              />
              <Button 
                onClick={handleAddUnavailableDate}
                disabled={saving || !newUnavailableDate}
                className="bg-[#194461] hover:bg-[#091D2E]"
                data-testid="add-date-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={18} />}
                Add
              </Button>
            </div>

            {/* Current unavailable dates */}
            <div>
              <Label className="text-sm font-medium text-slate-600 mb-2 block">
                Days Off ({(selectedDriver?.unavailable_dates || []).length})
              </Label>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {(selectedDriver?.unavailable_dates || []).sort().map(date => {
                  const isPast = date < getTodayString();
                  return (
                    <div 
                      key={date} 
                      className={`flex items-center justify-between p-2 rounded border ${
                        isPast ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <CalendarOff size={14} className={isPast ? 'text-slate-400' : 'text-amber-500'} />
                        <span className={isPast ? 'text-slate-500' : 'text-slate-700'}>
                          {formatDate(date)}
                        </span>
                        {isPast && <span className="text-xs text-slate-400">(past)</span>}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-400 hover:text-red-600"
                        onClick={() => handleRemoveUnavailableDate(date)}
                        data-testid={`remove-date-${date}`}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  );
                })}
                {(selectedDriver?.unavailable_dates || []).length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No days off scheduled
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvailabilityDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Driver</DialogTitle>
            <DialogDescription>
              Update driver information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={editForm.full_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                data-testid="edit-driver-name"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={editForm.phone}
                onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="423-555-0102"
                data-testid="edit-driver-phone"
              />
            </div>

            <div>
              <Label htmlFor="territory">Territory</Label>
              <Select 
                value={editForm.territory || 'none'} 
                onValueChange={(value) => setEditForm(prev => ({ ...prev, territory: value === 'none' ? '' : value }))}
              >
                <SelectTrigger data-testid="edit-driver-territory">
                  <SelectValue placeholder="Select territory" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Assigned</SelectItem>
                  {territories.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Used for smart route recommendations
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateDriver}
              disabled={saving || !editForm.full_name}
              className="bg-[#194461] hover:bg-[#091D2E]"
              data-testid="save-driver-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
